import { c as _c } from "react/compiler-runtime";
import { type VariantProps } from "tailwind-variants";
import * as React from "react";
import { HiCheckCircle, HiExclamationCircle, HiExclamationTriangle, HiInformationCircle } from "react-icons/hi2";
import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";
const alertVariants = tv({
  base: "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  variants: {
    variant: {
      default: ["bg-primary/10 dark:bg-primary/20", "border-primary/20 dark:border-primary/30", "text-primary-700 dark:text-primary-300", "[&>svg]:text-primary-700 dark:[&>svg]:text-primary-300"],
      success: ["bg-emerald-50 dark:bg-emerald-900/30", "border-emerald-200 dark:border-emerald-800", "text-emerald-900 dark:text-emerald-200", "[&>svg]:text-emerald-900 dark:[&>svg]:text-emerald-200"],
      error: ["bg-red-50 dark:bg-red-900/30", "border-red-200 dark:border-red-800", "text-red-900 dark:text-red-200", "[&>svg]:text-red-900 dark:[&>svg]:text-red-200"],
      warning: ["bg-yellow-50 dark:bg-yellow-900/30", "border-yellow-200 dark:border-yellow-800", "text-yellow-900 dark:text-yellow-200", "[&>svg]:text-yellow-900 dark:[&>svg]:text-yellow-200"],
      neutral: ["bg-muted", "border-border", "text-foreground", "[&>svg]:text-foreground"],
      destructive: ["bg-red-50 dark:bg-red-900/30", "border-red-200 dark:border-red-800", "text-red-900 dark:text-red-200", "[&>svg]:text-red-900 dark:[&>svg]:text-red-200"]
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const alertIcons = {
  default: HiInformationCircle,
  success: HiCheckCircle,
  error: HiExclamationCircle,
  warning: HiExclamationTriangle,
  neutral: HiInformationCircle,
  destructive: HiExclamationCircle
} as const;
const getDefaultIcon = (variant: VariantProps<typeof alertVariants>["variant"]): React.ElementType => {
  return alertIcons[variant ?? "default"];
};
interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  icon?: React.ElementType | React.ReactNode;
}
const Alert = React.forwardRef<HTMLDivElement, AlertProps>((t0, ref) => {
  const $ = _c(19);
  let children;
  let className;
  let icon;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      icon,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = props;
    $[5] = variant;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    props = $[4];
    variant = $[5];
  }
  let t1;
  if ($[6] !== variant) {
    t1 = getDefaultIcon(variant);
    $[6] = variant;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const defaultIcon = t1;
  const iconToRender = icon ?? defaultIcon;
  let t2;
  if ($[8] !== className || $[9] !== variant) {
    t2 = cx(alertVariants({
      variant
    }), className);
    $[8] = className;
    $[9] = variant;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== iconToRender) {
    t3 = React.isValidElement(iconToRender) ? React.cloneElement(iconToRender as React.ReactElement<React.SVGProps<SVGSVGElement>>, {
      className: cx("h-4 w-4 shrink-0", (iconToRender as React.ReactElement<React.SVGProps<SVGSVGElement>>).props.className),
      "aria-hidden": "true"
    }) : typeof iconToRender === "function" && React.createElement(iconToRender as React.ElementType, {
      className: "h-4 w-4 shrink-0",
      "aria-hidden": "true"
    });
    $[11] = iconToRender;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== children || $[14] !== props || $[15] !== ref || $[16] !== t2 || $[17] !== t3) {
    t4 = <div ref={ref} role="alert" className={t2} {...props}>{t3}{children}</div>;
    $[13] = children;
    $[14] = props;
    $[15] = ref;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
});
Alert.displayName = "Alert";
const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("mb-1 font-semibold leading-none tracking-tight", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <h5 ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
AlertTitle.displayName = "AlertTitle";
const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("text-body [&_p]:leading-relaxed", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
AlertDescription.displayName = "AlertDescription";
export { Alert, AlertDescription, AlertTitle, alertVariants, type AlertProps };